import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Breadcrumbs,
  Link as MuiLink,
  Paper,
  TextField,
  IconButton,
  Tooltip,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBackOutlined";
import RefreshIcon from "@mui/icons-material/RefreshOutlined";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpwardOutlined";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownwardOutlined";
import RouterIcon from "@mui/icons-material/RouterOutlined";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import DangerConfirmField, {
  isDangerCodeValid,
} from "../../components/common/DangerConfirmField";
import {
  fetchAssignments,
  searchDevices,
  assignDevice,
  removeDevice,
  moveDevice,
  resetForUser,
  setSearchQuery,
  clearSearchResults,
} from "./deviceAssignmentsSlice";

const Loc = ({ row }) => {
  const parts = [row.district, row.block, row.panchayat].filter(Boolean);
  return parts.length ? (
    <Typography variant="caption" color="text.secondary">
      {parts.join(" · ")}
    </Typography>
  ) : (
    <Typography variant="caption" color="text.disabled">
      —
    </Typography>
  );
};

export default function UserDeviceAssignmentsPage() {
  const { cid, uid } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const debounceRef = useRef(null);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [removeConfirm, setRemoveConfirm] = useState("");

  const {
    user,
    company,
    assigned,
    loading,
    saving,
    search,
    error,
  } = useSelector((s) => s.deviceAssignments);

  useEffect(() => {
    dispatch(resetForUser(uid));
    dispatch(fetchAssignments(uid));
  }, [uid, dispatch]);

  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  const onQueryChange = (e) => {
    const v = e.target.value;
    dispatch(setSearchQuery(v));
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (v.trim().length < 3) {
      dispatch(clearSearchResults());
      return;
    }
    debounceRef.current = setTimeout(() => {
      dispatch(searchDevices({ q: v.trim(), userId: uid }));
    }, 300);
  };

  const onAssign = async (row) => {
    const res = await dispatch(
      assignDevice({ userId: uid, deviceId: row.device_id })
    );
    if (assignDevice.fulfilled.match(res)) {
      toast.success(`Assigned ${row.label || row.device_imei}`);
    }
  };

  const confirmRemove = async () => {
    if (!removeTarget) return;
    if (!isDangerCodeValid(removeConfirm)) return;
    const row = removeTarget;
    const res = await dispatch(
      removeDevice({ userId: uid, deviceId: row.device_id })
    );
    setRemoveTarget(null);
    setRemoveConfirm("");
    if (removeDevice.fulfilled.match(res)) {
      toast.success(`Removed ${row.label || row.device_imei}`);
    }
  };

  const closeRemove = () => {
    setRemoveTarget(null);
    setRemoveConfirm("");
  };

  const onMove = async (row, direction) => {
    const res = await dispatch(
      moveDevice({ userId: uid, deviceId: row.device_id, direction })
    );
    if (moveDevice.fulfilled.match(res)) {
      const items = res.payload || [];
      const moved = items.find((d) => d.device_id === row.device_id);
      if (moved && moved.order === row.order) {
        toast(direction === "up" ? "Already at top" : "Already at bottom", {
          icon: "ℹ️",
        });
      }
    }
  };

  const refresh = () => dispatch(fetchAssignments(uid));

  const headerCounts = useMemo(
    () => `${assigned.length} assigned`,
    [assigned.length]
  );

  return (
    <Box>
      <Stack direction="row" alignItems="flex-start" spacing={1} mb={1.5}>
        <Tooltip title="Back to users">
          <IconButton
            size="small"
            onClick={() => navigate(`/companies/${cid}/users`)}
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Breadcrumbs separator="›" sx={{ fontSize: "0.6875rem", mb: 0.25 }}>
            <MuiLink
              component="button"
              underline="hover"
              color="text.secondary"
              onClick={() => navigate("/companies")}
              sx={{ fontSize: "0.6875rem" }}
            >
              Companies
            </MuiLink>
            <MuiLink
              component="button"
              underline="hover"
              color="text.secondary"
              onClick={() => navigate(`/companies/${cid}/details`)}
              sx={{ fontSize: "0.6875rem" }}
            >
              {company?.name || "…"}
            </MuiLink>
            <MuiLink
              component="button"
              underline="hover"
              color="text.secondary"
              onClick={() => navigate(`/companies/${cid}/users`)}
              sx={{ fontSize: "0.6875rem" }}
            >
              Users
            </MuiLink>
            <Typography
              variant="caption"
              color="text.primary"
              sx={{ fontSize: "0.6875rem" }}
            >
              {user?.name || user?.username || "…"} · Devices
            </Typography>
          </Breadcrumbs>
          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
            <Typography
              sx={{
                wordBreak: "break-word",
                fontSize: { xs: "0.9375rem", sm: "1.0625rem" },
                fontWeight: 700,
                letterSpacing: "-0.005em",
                lineHeight: 1.25,
              }}
            >
              {user ? `${user.name} - ${user.username}` : "—"}
            </Typography>
            <Chip
              label={headerCounts}
              size="small"
              sx={{
                height: 20,
                fontSize: "0.6875rem",
                fontWeight: 600,
                backgroundColor: "primary.light",
                color: "primary.main",
              }}
            />
          </Stack>
        </Box>
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={refresh}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1.5}
        alignItems="stretch"
      >
        <Paper variant="outlined" sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
          <Box
            sx={{
              p: 1,
              borderBottom: "1px solid",
              borderColor: "divider",
              backgroundColor: "action.hover",
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6 }}>
              Search devices
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} sx={{ p: 1, borderBottom: "1px solid", borderColor: "divider" }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search by company, IMEI, mfg id, district, block, panchayat… (min 3 chars)"
              value={search.q}
              onChange={onQueryChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: "text.secondary" }} />
                  </InputAdornment>
                ),
                endAdornment: search.loading ? (
                  <InputAdornment position="end">
                    <CircularProgress size={14} />
                  </InputAdornment>
                ) : null,
              }}
            />
          </Stack>
          {isMobile ? (
            <Box sx={{ flex: 1, p: 1, maxHeight: "55vh", overflowY: "auto" }}>
              {search.loading && search.results.length === 0 && (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <CircularProgress size={20} />
                </Box>
              )}
              {!search.loading && search.q.trim().length < 3 && (
                <Box sx={{ textAlign: "center", py: 5, color: "text.secondary" }}>
                  <RouterIcon sx={{ fontSize: 36, opacity: 0.4, mb: 0.5 }} />
                  <Typography variant="caption" display="block">
                    Type 3+ characters to search devices.
                  </Typography>
                </Box>
              )}
              {!search.loading &&
                search.q.trim().length >= 3 &&
                search.results.length === 0 && (
                  <Box sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
                    <Typography variant="caption">No matching devices.</Typography>
                  </Box>
                )}
              <Stack spacing={0.75}>
                {search.results.map((r, i) => (
                  <Paper
                    key={r.device_id}
                    variant="outlined"
                    sx={{
                      p: 1.25,
                      backgroundColor: "primary.main",
                      borderColor: "primary.dark",
                      transition: "background-color .15s, border-color .15s",
                      "& .MuiTypography-root": { color: "#ffffff" },
                      "& .MuiSvgIcon-root": { color: "#ccfbf1" },
                      "&:hover": {
                        backgroundColor: "primary.dark",
                        borderColor: "primary.main",
                      },
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="flex-start"
                      justifyContent="space-between"
                      spacing={1}
                      mb={0.5}
                    >
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, wordBreak: "break-word" }}
                        >
                          {r.label || "—"}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            fontFamily: "monospace",
                            color: "text.secondary",
                            fontSize: "0.7rem",
                          }}
                        >
                          {r.motor_imei || r.device_imei}
                        </Typography>
                      </Box>
                      {r.already_assigned ? (
                        <Chip
                          label="Assigned"
                          size="small"
                          sx={{
                            height: 22,
                            fontSize: "0.6875rem",
                            fontWeight: 600,
                            backgroundColor: "success.light",
                            color: "success.dark",
                            flexShrink: 0,
                          }}
                        />
                      ) : (
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                          disabled={saving}
                          onClick={() => onAssign(r)}
                          sx={{ minHeight: 28, flexShrink: 0 }}
                        >
                          Assign
                        </Button>
                      )}
                    </Stack>
                    <Loc row={r} />
                  </Paper>
                ))}
              </Stack>
            </Box>
          ) : (
            <TableContainer sx={{ flex: 1, maxHeight: "calc(100vh - 260px)" }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 36 }}>#</TableCell>
                    <TableCell>Company</TableCell>
                    <TableCell>Motor IMEI</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell sx={{ width: 110 }} align="right">
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {search.loading && search.results.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                        <CircularProgress size={20} />
                      </TableCell>
                    </TableRow>
                  )}
                  {!search.loading && search.q.trim().length < 3 && (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        align="center"
                        sx={{ py: 4, color: "text.secondary" }}
                      >
                        <RouterIcon sx={{ fontSize: 28, opacity: 0.4, mb: 0.5 }} />
                        <Typography variant="caption" display="block">
                          Type 3+ characters to search devices.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {!search.loading &&
                    search.q.trim().length >= 3 &&
                    search.results.length === 0 && (
                      <TableRow>
                        <TableCell
                          colSpan={5}
                          align="center"
                          sx={{ py: 4, color: "text.secondary" }}
                        >
                          No matching devices.
                        </TableCell>
                      </TableRow>
                    )}
                  {search.results.map((r, i) => (
                    <TableRow key={r.device_id} hover>
                      <TableCell sx={{ color: "text.secondary" }}>{i + 1}</TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>
                        {r.label || "—"}
                      </TableCell>
                      <TableCell sx={{ fontFamily: "monospace", fontSize: "0.7rem" }}>
                        {r.motor_imei || r.device_imei}
                      </TableCell>
                      <TableCell>
                        <Loc row={r} />
                      </TableCell>
                      <TableCell align="right">
                        {r.already_assigned ? (
                          <Chip
                            label="Assigned"
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: "0.6875rem",
                              borderRadius: "2px",
                              backgroundColor: "#e8f5ec",
                              color: "#107e3e",
                              "& .MuiChip-label": { px: 0.75 },
                            }}
                          />
                        ) : (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                            disabled={saving}
                            onClick={() => onAssign(r)}
                            sx={{ fontSize: "0.6875rem", py: 0.25, minHeight: 0 }}
                          >
                            Assign
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        <Paper variant="outlined" sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
          <Box
            sx={{
              p: 1,
              borderBottom: "1px solid",
              borderColor: "divider",
              backgroundColor: "action.hover",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6 }}>
              Assigned devices ({assigned.length})
            </Typography>
            {saving && <CircularProgress size={14} />}
          </Box>
          {isMobile ? (
            <Box sx={{ flex: 1, p: 1, maxHeight: "55vh", overflowY: "auto" }}>
              {loading && assigned.length === 0 && (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <CircularProgress size={20} />
                </Box>
              )}
              {!loading && assigned.length === 0 && (
                <Box sx={{ textAlign: "center", py: 5, color: "text.secondary" }}>
                  <RouterIcon sx={{ fontSize: 36, opacity: 0.4, mb: 0.5 }} />
                  <Typography variant="caption" display="block">
                    No devices assigned. Search above and tap Assign.
                  </Typography>
                </Box>
              )}
              <Stack spacing={0.75}>
                {assigned.map((r, i) => (
                  <Paper
                    key={r.device_id}
                    variant="outlined"
                    sx={{
                      p: 1.25,
                      backgroundColor: "primary.main",
                      borderColor: "primary.dark",
                      "& .MuiTypography-root": { color: "#ffffff" },
                      "& .MuiSvgIcon-root": { color: "#ccfbf1" },
                    }}
                  >
                    <Stack direction="row" alignItems="flex-start" spacing={1} mb={0.75}>
                      <Box
                        sx={{
                          width: 26,
                          height: 26,
                          borderRadius: "8px",
                          backgroundColor: "primary.light",
                          color: "primary.main",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "0.75rem",
                          fontWeight: 700,
                          flexShrink: 0,
                        }}
                      >
                        {r.order ?? i + 1}
                      </Box>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, wordBreak: "break-word" }}
                        >
                          {r.label || "—"}
                        </Typography>
                        <Loc row={r} />
                      </Box>
                    </Stack>
                    <Stack spacing={0.25} mb={1}>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 600, color: "text.secondary", minWidth: 40, fontSize: "0.625rem", textTransform: "uppercase", letterSpacing: 0.4 }}
                        >
                          Motor
                        </Typography>
                        <Typography variant="caption" sx={{ fontFamily: "monospace", fontSize: "0.7rem" }}>
                          {r.motor_imei || r.device_imei}
                        </Typography>
                      </Stack>
                      {r.tank_imei && (
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 600, color: "text.secondary", minWidth: 40, fontSize: "0.625rem", textTransform: "uppercase", letterSpacing: 0.4 }}
                          >
                            Tank
                          </Typography>
                          <Typography variant="caption" sx={{ fontFamily: "monospace", fontSize: "0.7rem" }}>
                            {r.tank_imei}
                          </Typography>
                        </Stack>
                      )}
                    </Stack>
                    <Divider sx={{ mb: 0.75 }} />
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Stack direction="row" spacing={0.25}>
                        <IconButton
                          size="small"
                          disabled={saving || i === 0}
                          onClick={() => onMove(r, "up")}
                        >
                          <ArrowUpwardIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          disabled={saving || i === assigned.length - 1}
                          onClick={() => onMove(r, "down")}
                        >
                          <ArrowDownwardIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                      <Button
                        size="small"
                        variant="text"
                        color="error"
                        startIcon={<DeleteIcon fontSize="small" />}
                        disabled={saving}
                        onClick={() => setRemoveTarget(r)}
                        sx={{ fontSize: "0.75rem" }}
                      >
                        Remove
                      </Button>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </Box>
          ) : (
            <TableContainer sx={{ flex: 1, maxHeight: "calc(100vh - 220px)" }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 36 }}>#</TableCell>
                    <TableCell>Company</TableCell>
                    <TableCell>Motor IMEI</TableCell>
                    <TableCell>Tank IMEI</TableCell>
                    <TableCell sx={{ width: 110 }} align="right">
                      Order
                    </TableCell>
                    <TableCell sx={{ width: 60 }} align="right">
                      Remove
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading && assigned.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <CircularProgress size={20} />
                      </TableCell>
                    </TableRow>
                  )}
                  {!loading && assigned.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        align="center"
                        sx={{ py: 5, color: "text.secondary" }}
                      >
                        <RouterIcon sx={{ fontSize: 32, opacity: 0.4, mb: 0.5 }} />
                        <Typography variant="caption" display="block">
                          No devices assigned. Search the left panel and click
                          Assign.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {assigned.map((r, i) => (
                    <TableRow key={r.device_id} hover>
                      <TableCell sx={{ color: "text.secondary" }}>{r.order ?? i + 1}</TableCell>
                      <TableCell>
                        <Box sx={{ fontWeight: 500 }}>{r.label || "—"}</Box>
                        <Loc row={r} />
                      </TableCell>
                      <TableCell sx={{ fontFamily: "monospace", fontSize: "0.7rem" }}>
                        {r.motor_imei || r.device_imei}
                      </TableCell>
                      <TableCell sx={{ fontFamily: "monospace", fontSize: "0.7rem" }}>
                        {r.tank_imei || "—"}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                        <Tooltip title="Move up">
                          <span>
                            <IconButton
                              size="small"
                              disabled={saving || i === 0}
                              onClick={() => onMove(r, "up")}
                            >
                              <ArrowUpwardIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Move down">
                          <span>
                            <IconButton
                              size="small"
                              disabled={saving || i === assigned.length - 1}
                              onClick={() => onMove(r, "down")}
                            >
                              <ArrowDownwardIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Remove">
                          <span>
                            <IconButton
                              size="small"
                              disabled={saving}
                              onClick={() => setRemoveTarget(r)}
                            >
                              <DeleteIcon
                                fontSize="small"
                                sx={{ color: "error.main" }}
                              />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Stack>

      <Dialog
        open={Boolean(removeTarget)}
        onClose={closeRemove}
        PaperProps={{ sx: { borderRadius: "10px", minWidth: { xs: 0, sm: 380 } } }}
        fullWidth
      >
        <DialogTitle sx={{ p: 1.75, borderBottom: "1px solid", borderColor: "divider" }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "error.main" }}>
            Remove Device
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Remove <b>{removeTarget?.label || removeTarget?.device_imei}</b>
            {removeTarget?.motor_imei ? ` (${removeTarget.motor_imei})` : ""}
            {" "}from <b>{user?.name || user?.username}</b>?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The device will be unassigned from this user and the order list
            will be re-numbered. Other users with this device keep their
            assignment.
          </Typography>
          <DangerConfirmField
            value={removeConfirm}
            onChange={setRemoveConfirm}
            disabled={saving}
          />
        </DialogContent>
        <DialogActions sx={{ p: 1.25, borderTop: "1px solid", borderColor: "divider" }}>
          <Button onClick={closeRemove} variant="outlined" disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={confirmRemove}
            variant="contained"
            color="error"
            disabled={saving || !isDangerCodeValid(removeConfirm)}
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
          >
            {saving ? "Removing…" : "Remove"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
